import { ApiError, request, authorizedRequest, getValidationErrors } from "../../../services/apiClient";
import { buildQueryString } from "../../../utils/queryParams";
import { unwrapArrayResponse } from "../../../utils/apiHelpers";
import { getActiveLocale } from "../../../i18n";
import { getInitialThemeState } from "../../../providers/ThemeProvider";
import type {
  AuthLoginPayload,
  AuthRegisterPayload,
  AuthResponse,
  FacebookAuthUrlResponse,
  FacebookCallbackRequest,
  GoogleAuthUrlResponse,
  GoogleCallbackRequest,
  OAuthConnection,
  ResetPasswordPayload,
  UpdateProfilePayload,
  UpdateProfileResult,
  User,
  RefreshTokenRequest,
  SessionView,
  ThemePreference
} from "@/types/auth";

interface MessageResponse {
  message?: string;
}

export async function requestPasswordReset(email: string): Promise<string | undefined> {
  const response = await request<MessageResponse>("/auth/password/forgot", {
    method: "POST",
    body: JSON.stringify({ email })
  });
  return response.message;
}

export async function resetPassword(payload: ResetPasswordPayload): Promise<string | undefined> {
  const response = await request<MessageResponse>("/auth/password/reset", {
    method: "POST",
    body: JSON.stringify(payload)
  });
  return response.message;
}

export async function registerUser(
  payload: AuthRegisterPayload
): Promise<AuthResponse> {
  const resolvedLocale = payload.preferred_locale ?? getActiveLocale();
  const resolvedTheme =
    payload.preferences?.appearance?.theme ?? getInitialThemeState().theme;
  const fullName = payload.full_name.trim();
  const displayName =
    payload.name?.trim() ||
    fullName.split(/\s+/).filter(Boolean)[0] ||
    fullName;

  return request<AuthResponse>("/auth/register", {
    method: "POST",
    body: JSON.stringify({
      email: payload.email,
      password: payload.password,
      name: displayName,
      full_name: fullName,
      device_label: payload.device_label,
      preferred_locale: resolvedLocale,
      preferences: {
        ...payload.preferences,
        appearance: {
          ...payload.preferences?.appearance,
          theme: resolvedTheme
        }
      }
    })
  });
}

export async function loginUser(
  payload: AuthLoginPayload
): Promise<AuthResponse> {
  const identifier = payload.identifier ?? payload.email;

  return request<AuthResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({
      email: payload.email,
      identifier,
      password: payload.password,
      remember_me: payload.remember_me ?? false,
      device_label: payload.device_label
    })
  });
}

export async function logoutUser(accessToken: string): Promise<void> {
  await authorizedRequest(accessToken, "/auth/logout", {
    method: "POST",
    body: JSON.stringify({})
  });
}

export async function refreshSessionTokens(
  payload: RefreshTokenRequest
): Promise<AuthResponse> {
  return request<AuthResponse>("/auth/refresh", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function getCurrentUser(accessToken: string): Promise<User> {
  return authorizedRequest<User>(accessToken, "/users/me");
}

export async function updateCurrentUser(
  accessToken: string,
  payload: UpdateProfilePayload
): Promise<UpdateProfileResult> {
  const response = await authorizedRequest<unknown>(accessToken, "/users/me", {
    method: "PUT",
    body: JSON.stringify(payload)
  });

  if (isUser(response)) {
    return { kind: "updated", user: response };
  }

  if (
    response &&
    typeof response === "object" &&
    "message" in response &&
    typeof (response as { message: unknown }).message === "string"
  ) {
    const data = response as { message: string; pending_email?: string };
    return {
      kind: "email_verification",
      message: data.message,
      pending_email: data.pending_email ?? undefined
    };
  }

  throw new ApiError(
    500,
    "Unexpected response when updating profile. Please try again."
  );
}

export async function uploadProfilePhoto(
  accessToken: string,
  file: File
): Promise<User> {
  const formData = new FormData();
  formData.append("file", file);

  return authorizedRequest<User>(accessToken, "/users/me/profile-photo", {
    method: "PUT",
    body: formData
  });
}

export async function requestPhoneVerification(
  accessToken: string,
  phone: string
): Promise<string | undefined> {
  const response = await authorizedRequest<MessageResponse>(
    accessToken,
    "/auth/phone/request",
    {
      method: "POST",
      body: JSON.stringify({ phone })
    }
  );
  return response.message;
}

export async function confirmPhoneVerification(
  accessToken: string,
  code: string
): Promise<User> {
  return authorizedRequest<User>(accessToken, "/auth/phone/verify", {
    method: "POST",
    body: JSON.stringify({ code })
  });
}

export async function bypassPhoneVerification(
  accessToken: string
): Promise<User> {
  return authorizedRequest<User>(accessToken, "/auth/phone/dev-verify", {
    method: "POST"
  });
}

export async function bypassEmailVerification(
  accessToken: string
): Promise<User> {
  return authorizedRequest<User>(accessToken, "/auth/email/dev-verify", {
    method: "POST"
  });
}

export interface OAuthAuthUrlOptions {
  locale?: string;
  theme?: ThemePreference;
}

function buildOAuthOriginPath(
  path: string,
  options?: OAuthAuthUrlOptions
): string {
  const queryParams: Record<string, string | undefined> = {
    locale: options?.locale,
    theme: options?.theme
  };

  if (typeof window !== "undefined" && window.location?.origin) {
    queryParams.origin = window.location.origin;
  }

  const queryString = buildQueryString(queryParams);
  return `${path}${queryString}`;
}

export async function getGoogleAuthUrl(
  options?: OAuthAuthUrlOptions
): Promise<GoogleAuthUrlResponse> {
  const endpoint = buildOAuthOriginPath("/auth/google/login", options);
  return request<GoogleAuthUrlResponse>(endpoint, {
    method: "GET"
  });
}

export async function handleGoogleCallback(
  payload: GoogleCallbackRequest
): Promise<AuthResponse> {
  return request<AuthResponse>("/auth/google/callback", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function linkGoogleAccount(
  accessToken: string,
  payload: GoogleCallbackRequest
): Promise<void> {
  await authorizedRequest(accessToken, "/auth/google/link", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function listOAuthConnections(
  accessToken: string
): Promise<OAuthConnection[]> {
  const response = await authorizedRequest<
    OAuthConnection[] | { connections: OAuthConnection[] }
  >(accessToken, "/auth/oauth/connections");

  return unwrapArrayResponse<OAuthConnection>(response, "connections");
}

export async function unlinkOAuthProvider(
  accessToken: string,
  provider: string
): Promise<void> {
  await authorizedRequest(accessToken, `/auth/oauth/${provider}`, {
    method: "DELETE"
  });
}

export async function listSessions(
  accessToken: string
): Promise<SessionView[]> {
  return authorizedRequest(accessToken, "/auth/sessions");
}

export async function revokeSession(
  accessToken: string,
  sessionId: string
): Promise<void> {
  await authorizedRequest(accessToken, `/auth/sessions/${sessionId}/revoke`, {
    method: "POST"
  });
}

export async function revokeAllSessions(accessToken: string): Promise<void> {
  await authorizedRequest(accessToken, "/auth/sessions/revoke-all", {
    method: "POST"
  });
}

export async function resendVerificationEmail(
  accessToken: string,
  email: string
): Promise<{ message: string }> {
  return authorizedRequest<{ message: string }>(
    accessToken,
    "/auth/resend-verification",
    {
      method: "POST",
      body: JSON.stringify({ email })
    }
  );
}

export async function verifyEmailToken(
  token: string
): Promise<AuthResponse> {
  return request<AuthResponse>("/auth/verify-email", {
    method: "POST",
    body: JSON.stringify({ token })
  });
}

function isUser(value: unknown): value is User {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.email === "string" &&
    typeof candidate.name === "string"
  );
}

// Facebook OAuth2 Functions
export async function getFacebookAuthUrl(
  options?: OAuthAuthUrlOptions
): Promise<FacebookAuthUrlResponse> {
  const endpoint = buildOAuthOriginPath("/auth/facebook/login", options);
  return request<FacebookAuthUrlResponse>(endpoint, {
    method: "GET"
  });
}

export async function handleFacebookCallback(
  payload: FacebookCallbackRequest
): Promise<AuthResponse> {
  return request<AuthResponse>("/auth/facebook/callback", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function linkFacebookAccount(
  accessToken: string,
  payload: FacebookCallbackRequest
): Promise<void> {
  await authorizedRequest(accessToken, "/auth/facebook/link", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export { ApiError, getValidationErrors };
